import React, { useEffect, useState } from 'react';
import { Briefcase, MessageSquare, AlertCircle, CheckCircle, CreditCard, Upload, FileText } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { listPortalMatters, listPortalInvoices, listPortalMessages, listPortalMatterPayments, listPortalMatterCharges, listPortalDocumentRequestsForClient } from '../../lib/db';
import { formatDate, formatMoney } from '../../lib/formatters';
import { useRoute } from '../../lib/router';

const KPI = ({ icon: Icon, label, value, sub, color, onClick }) => (
  <button
    onClick={onClick}
    className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 text-left hover:shadow-md transition w-full"
  >
    <div className={`inline-flex p-2 rounded-xl mb-3 ${color}`}>
      <Icon size={18} className="text-white" />
    </div>
    <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{label}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </button>
);

const STATUS_COLOR = {
  open:        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  closed:      'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  archived:    'bg-slate-100 text-slate-500',
};

const INV_COLOR = {
  sent:    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  paid:    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const INV_LABEL = {
  sent:    'Pending Payment',
  paid:    'Paid',
  overdue: 'Overdue',
};

export default function PortalDashboardPage() {
  const { contact } = usePortal();
  const [, navigate] = useRoute();
  const [matters,   setMatters]   = useState([]);
  const [invoices,  setInvoices]  = useState([]);
  const [payments,  setPayments]  = useState([]);
  const [charges,   setCharges]   = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [unread,    setUnread]    = useState(0);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    if (!contact) return;
    Promise.all([
      listPortalMatters(),
      listPortalInvoices(),
      listPortalMessages(contact.id),
      listPortalMatterPayments(),
      listPortalMatterCharges(),
      listPortalDocumentRequestsForClient(),
    ]).then(([m, inv, msgs, pay, chrg, reqs]) => {
      setMatters(m.data     || []);
      setInvoices(inv.data  || []);
      setPayments(pay.data  || []);
      setCharges(chrg.data  || []);
      setDocRequests(Array.isArray(reqs.data) ? reqs.data : []);
      const unreadCount = (msgs.data || []).filter(
        (msg) => msg.sender_type === 'firm' && !msg.read_at
      ).length;
      setUnread(unreadCount);
      setLoading(false);
    });
  }, [contact]);

  const pendingDocRequests = docRequests.filter(
    (r) => r.status === 'open' || r.status === 'rejected'
  );

  const openMatters     = matters.filter((m) => m.status === 'open' || m.status === 'in_progress');
  const overdueInvoices = invoices.filter((i) => i.status === 'overdue');

  // ── Matter-level billing totals (flat fee + charges - payments) ──────────
  // This reflects the actual financial picture regardless of invoice status.
  const totalAgreedFee = matters.reduce((s, m) =>
    s + Number(m.flat_fee_amount || m.agreed_fee || 0), 0);
  const totalCharges   = charges.reduce((s, c) => s + Number(c.amount || 0), 0);
  const totalBilled    = totalAgreedFee + totalCharges;
  const totalPaid      = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalDue       = Math.max(0, totalBilled - totalPaid);

  // Progress toward paying off agreed fees + charges
  const paidPct = totalBilled > 0 ? Math.min(100, Math.round((totalPaid / totalBilled) * 100)) : 0;

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div>
          <div className="h-8 w-1/2 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
          <div className="h-4 w-1/3 bg-slate-200 dark:bg-slate-700 rounded" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
              <div className="h-10 w-10 rounded-xl bg-slate-200 dark:bg-slate-700 mb-3" />
              <div className="h-6 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-1" />
              <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          ))}
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
          <div className="h-4 w-1/3 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
          <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded mb-4" />
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i}>
                <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-1" />
                <div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
          Welcome back, {contact?.first_name || 'there'}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
          Here's a summary of your case files and account.
        </p>
      </div>

      {/* Document requests action banner */}
      {pendingDocRequests.length > 0 && (
        <section className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
                <Upload size={18} className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  Your attorney has requested {pendingDocRequests.length} document{pendingDocRequests.length !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  Open the matter and use the Requests tab to upload.
                </p>
                <ul className="mt-2 space-y-1">
                  {pendingDocRequests.slice(0, 3).map((r) => (
                    <li
                      key={r.id}
                      className="text-xs text-amber-900 dark:text-amber-200 flex items-center gap-2"
                    >
                      <FileText size={12} className="flex-shrink-0 opacity-60" />
                      <button
                        onClick={() => r.matter_id && navigate(`/portal/matters/${r.matter_id}`)}
                        className="hover:underline text-left truncate"
                      >
                        {r.title}
                      </button>
                      {r.due_at && (
                        <span className="text-amber-700 dark:text-amber-400 flex-shrink-0">
                          · due {formatDate(r.due_at)}
                        </span>
                      )}
                    </li>
                  ))}
                  {pendingDocRequests.length > 3 && (
                    <li className="text-xs text-amber-700 dark:text-amber-400">
                      + {pendingDocRequests.length - 3} more…
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPI
          icon={Briefcase}
          label="Active case files"
          value={openMatters.length}
          color="bg-indigo-500"
          onClick={() => navigate('/portal/matters')}
        />
        <KPI
          icon={AlertCircle}
          label="Amount due"
          value={formatMoney(totalDue)}
          sub={overdueInvoices.length > 0 ? `${overdueInvoices.length} overdue` : totalDue > 0 ? 'outstanding balance' : 'all settled'}
          color={overdueInvoices.length > 0 ? 'bg-red-500' : 'bg-amber-500'}
          onClick={() => navigate('/portal/invoices')}
        />
        <KPI
          icon={CheckCircle}
          label="Total paid"
          value={formatMoney(totalPaid)}
          sub={`${payments.length} payment${payments.length !== 1 ? 's' : ''} recorded`}
          color="bg-emerald-500"
          onClick={() => navigate('/portal/invoices')}
        />
        <KPI
          icon={MessageSquare}
          label="Unread messages"
          value={unread}
          color="bg-purple-500"
          onClick={() => navigate('/portal/messages')}
        />
      </div>

      {/* Billing summary bar */}
      {totalBilled > 0 && (
        <section className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <CreditCard size={15} className="text-slate-400" /> Billing summary
            </h2>
            <button
              onClick={() => navigate('/portal/invoices')}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              View invoices
            </button>
          </div>

          {/* Progress bar */}
          <div className="mt-3 mb-4">
            <div className="flex justify-between text-xs text-slate-500 mb-1.5">
              <span>{paidPct}% paid</span>
              <span>{formatMoney(totalPaid)} of {formatMoney(totalBilled)}</span>
            </div>
            <div className="h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${paidPct}%` }}
              />
            </div>
          </div>

          {/* Three figures in a row */}
          <div className="grid grid-cols-3 divide-x divide-slate-100 dark:divide-slate-800">
            <div className="pr-4">
              <p className="text-xs text-slate-400 mb-0.5">Total billed</p>
              <p className="text-base font-bold text-slate-900 dark:text-white">{formatMoney(totalBilled)}</p>
            </div>
            <div className="px-4">
              <p className="text-xs text-slate-400 mb-0.5">Total paid</p>
              <p className="text-base font-bold text-emerald-600 dark:text-emerald-400">{formatMoney(totalPaid)}</p>
            </div>
            <div className="pl-4">
              <p className="text-xs text-slate-400 mb-0.5">Balance due</p>
              <p className={`text-base font-bold ${totalDue > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {formatMoney(totalDue)}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Recent matters */}
      {matters.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Your case files</h2>
            <button onClick={() => navigate('/portal/matters')} className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">View all</button>
          </div>
          <div className="space-y-2">
            {matters.slice(0, 5).map((m) => (
              <div
                key={m.id}
                onClick={() => navigate(`/portal/matters/${m.id}`)}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 flex items-center justify-between gap-4 cursor-pointer hover:shadow-sm transition"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{m.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{m.matter_number} · {m.practice_area || '—'}</p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${STATUS_COLOR[m.status] || STATUS_COLOR.open}`}>
                  {m.status?.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent invoices */}
      {invoices.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Recent invoices</h2>
            <button onClick={() => navigate('/portal/invoices')} className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">View all</button>
          </div>
          <div className="space-y-2">
            {invoices.slice(0, 5).map((inv) => (
              <div
                key={inv.id}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Invoice #{inv.invoice_number}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {inv.matter?.title || '—'} · Due {formatDate(inv.due_date)}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${INV_COLOR[inv.status] || 'bg-slate-100 text-slate-500'}`}>
                    {INV_LABEL[inv.status] || inv.status}
                  </span>
                  {inv.total > 0 && (
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">
                      {formatMoney(inv.total)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
